import { memo, useMemo } from "react";
import type { AvailableForm, Script, ScriptForm } from "@/features/dashboard/types";
import { VersionColumn } from "@/features/versions/components";
import { RowHeader } from "./RowHeader";
import { FormScoreCell } from "./FormScoreCell";
import { AddFormCell } from "./AddFormCell";
import type { ScriptRowMeta, VisibleForm } from "./types";

interface ScoreTableRowProps {
  row: ScriptRowMeta;
  columnWidth: string;
  formColumns: VisibleForm[];
  availableForms: AvailableForm[];
  addingForm: { scriptId: string; formId: string } | null;
  onAddForm: (script: Script, formId: string) => Promise<void> | void;
  onShare: (script: Script, scriptForm: ScriptForm) => void;
  onAnalyze: (scriptForm: ScriptForm, scriptName: string, script: Script) => void;
  language: string;
  formatDate: (dateString: string) => string;
  onVersionChange: (versionId: string) => void;
  onOpenVersionControl: () => void;
}

export const ScoreTableRow = memo(({
  row,
  columnWidth,
  formColumns,
  availableForms,
  addingForm,
  onAddForm,
  onShare,
  onAnalyze,
  language,
  formatDate,
  onVersionChange,
  onOpenVersionControl,
}: ScoreTableRowProps) => {
  const helper = useMemo(() => ({
    formsById: row.formsById,
    scoresByFormId: row.scoresByFormId,
  }), [row.formsById, row.scoresByFormId]);

  const script = row.script;

  return (
    <div
      className="grid items-stretch gap-4 border-b border-border/60 bg-card/80 px-4 py-3.5 transition-all hover:bg-muted/40 last:border-b-0"
      style={{ gridTemplateColumns: `repeat(${formColumns.length + 2}, ${columnWidth})` }}
    >
      <div className="flex h-full flex-col justify-center rounded-xl border border-border/80 bg-muted/30 p-3.5 text-foreground shadow-2xs">
        <RowHeader script={script} formatDate={formatDate} lastUpdated={row.lastUpdated} />
      </div>
      <div className="flex h-full flex-col justify-center rounded-xl border border-border/80 bg-muted/30 p-3 shadow-2xs">
        <VersionColumn
          script={script}
          selectedVersionId={row.activeVersionId ?? undefined}
          onVersionChange={onVersionChange}
          onOpenVersionControl={onOpenVersionControl}
          formatDate={formatDate}
        />
      </div>
      {formColumns.map((form) => {
        const scriptForm = helper.formsById.get(form.id);
        const score = helper.scoresByFormId.get(form.id) ?? null;
        const canLinkForm = availableForms.some((available) => available.id === form.id);
        const isAddingThisForm = addingForm?.scriptId === script.id && addingForm?.formId === form.id;
        const displayName = language === "th" && form.nameTh ? form.nameTh : form.name;

        if (!scriptForm) {
          return (
            <div key={form.id} className="flex items-center justify-center">
              <AddFormCell
                script={script}
                formId={form.id}
                displayName={displayName}
                canLinkForm={canLinkForm}
                isAdding={isAddingThisForm}
                isGlobalAddDisabled={Boolean(addingForm)}
                onAddForm={onAddForm}
              />
            </div>
          );
        }

        return (
          <div key={form.id} className="flex items-center justify-center">
            <FormScoreCell
              script={script}
              scriptForm={scriptForm}
              score={score}
              onShare={onShare}
              onAnalyze={onAnalyze}
              language={language}
            />
          </div>
        );
      })}
    </div>
  );
});

ScoreTableRow.displayName = "ScoreTableRow";
